<template>
  <v-container class="pa-0 my-0">
    <v-card class="mx-auto elevation-0 mb-5" outlined max-width="1540">
      <v-data-table
        :headers="headers"
        :items="services"
        :search="search"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': itemsOptions }"
        :sort-by="['name']"
        :sort-desc="[false]"
        item-key="key"
        :loading="loadingTable"
        loading-text="Atualizando lista de serviços... Por favor, aguarde"
        :height="tableHeight - 30"
      >
        <template v-slot:top>
          <v-row
            no-gutters
            class="d-flex align-center px-5 py-3"
            style="border-bottom: 1px solid rgba(0, 0, 0, 0.12)"
          >
            <h2 class="font-weight-regular">Gerenciar serviços</h2>
            <v-spacer></v-spacer>
            <v-btn
              :block="$vuetify.breakpoint.smAndDown"
              large
              color="accent"
              outlined
              :class="`px-4 text-none elevation-0 text-body-1 font-weight-bold ${
                $vuetify.breakpoint.smAndDown ? 'my-2' : 'mr-4'
              }`"
              to="/reseller/clients"
            >
              <v-icon v-text="'ph-fill ph-users'" left />
              Gerenciar clientes
            </v-btn>
            <v-btn
              large
              :block="$vuetify.breakpoint.smAndDown"
              color="accent"
              dark
              :class="`px-4 text-none elevation-0 text-body-1 font-weight-bold ${
                $vuetify.breakpoint.smAndDown ? 'mb-2' : ''
              }`"
              @click.capture="createNewPlanOrService(true)"
            >
              <v-icon v-text="'ph-plus'" left />
              Novo serviço
            </v-btn>
          </v-row>

          <v-row class="ma-0 pa-0">
            <v-col class="pa-0 d-flex align-center">
              <v-text-field
                v-model="search"
                prepend-inner-icon="ph-magnifying-glass"
                label="Pesquisar por serviço"
                single-line
                hide-details
                :style="$vuetify.breakpoint.smAndDown ? '' : 'max-width: 300px'"
                class="py-3 px-4 ma-0"
                color="grey"
                :loading="loadingTable"
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider />
        </template>

        <template v-slot:[`item.name`]="{ item }">
          <span
            class="blue--text text--darken-2 text-body-2 font-weight-medium link"
            @click="selectPlan(item)"
            >{{ item.name }}
          </span>
        </template>

        <template v-slot:[`item.price`]="{ item }">
          {{ formatMoney(item.price) }}
        </template>

        <template v-slot:[`item.is_active`]="{ item }">
          <v-progress-circular
            indeterminate
            color="grey"
            v-if="loadingTable"
            width="3"
            size="20"
          ></v-progress-circular>

          <div v-else>
            <v-chip
              :color="item.is_active ? 'success' : 'secondary lighten-3'"
              small
              class="font-weight-medium"
            >
              {{ item.is_active ? "Ativo" : "Desativado" }}
              <TooltipIcon
                icon="ph-pencil-simple"
                color="white"
                dark
                :label="
                  item.is_active
                    ? 'Alterar para Desativado'
                    : 'Alterar para Ativo'
                "
                button
                class="ml-2"
                size="18"
                xs
                @action="activeOrDeactivePlan(item)"
              />
            </v-chip>
          </div>
        </template>

        <template v-slot:[`item.is_visible`]="{ item }">
          <v-progress-circular
            indeterminate
            color="grey"
            v-if="loadingTable"
            width="3"
            size="20"
          ></v-progress-circular>
          <div v-else>
            <span v-if="item.is_service" class="font-weight-medium">
              <v-tooltip
                v-if="$vuetify.breakpoint.smAndUp"
                bottom
                max-width="200"
              >
                <template v-slot:activator="{ on, attrs }">
                  <!-- TODO: ter essa logo salva no respositório -->
                  <v-img
                    src="https://i1.wp.com/conectanuvem.com.br/wp-content/uploads/2021/04/cropped-Conecta-Nuvem-Eleve-os-resultados.png?fit=512%2C512&ssl=1"
                    contain
                    width="40"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <span class="caption"
                  >Apenas Conecta Nuvem pode ver esse serviço</span
                >
              </v-tooltip>
              <span v-else>Apenas Conecta</span>
            </span>
            <span v-else-if="item.is_visible" class="font-weight-medium">
              Todos
            </span>
            <span v-else class="font-italic"> Apenas revendedores </span>
          </div>
        </template>

        <template v-slot:[`item.sku`]="{ item }">
          <v-chip
            label
            class="font-weight-medium secondary--text text--lighten-2"
          >
            {{ item.sku }}
          </v-chip>
        </template>

        <template v-slot:[`item.rank`]="{ item }">
          <div class="text-right">{{ item.rank }}</div>
        </template>

        <template v-slot:[`item.created_date`]="{ item }">
          {{ formatDateFullMonth(item.created_date) }}
        </template>

        <template v-slot:[`item.options`]="{ item }">
          <v-card-actions>
            <TooltipIcon
              icon="ph-pencil-simple"
              color="secondary lighten-2"
              label="Editar serviço"
              class="mx-2"
              size="24"
              button
              @action="selectPlan(item)"
            />
          </v-card-actions>
        </template>
      </v-data-table>
    </v-card>

    <!-- CRIAÇÃO DE UM SERVIÇO -->

    <v-dialog
      v-model="showHideServiceDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card tile color="grey lighten-3">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="resetEditAddDialog()">
            <v-icon v-text="'ph-x'" />
          </v-btn>
          <v-toolbar-title
            v-text="`${editService ? 'Editar serviço' : 'Criar serviço'}`"
          />
          <v-spacer />
        </v-toolbar>

        <v-stepper v-model="addPlanSteps" class="py-0">
          <v-stepper-header
            class="d-flex justify-start py-2"
            style="max-height: 50px"
          >
            <v-stepper-step
              :complete="addPlanSteps > 1"
              step="1"
              class="ml-4 mr-6"
              :style="addPlanSteps > 1 ? 'cursor:pointer' : ''"
              @click="addPlanSteps > 1 ? (addPlanSteps = 1) : ''"
            >
              Informações sobre o {{ planOrServiceLabel }}
            </v-stepper-step>

            <v-stepper-step
              v-if="!is_service"
              :complete="addPlanSteps > 2"
              step="2"
              class="mr-6"
              :style="addPlanSteps === 3 ? 'cursor:pointer' : ''"
              @click="addPlanSteps === 3 ? (addPlanSteps = 2) : ''"
            >
              Selecionar produtos
            </v-stepper-step>

            <v-stepper-step
              :step="is_service ? 2 : 3"
              class="mr-6"
              @click="
                plan.name !== '' && products.length > 1
                  ? (addPlanSteps = 3)
                  : ''
              "
              :style="
                plan.name !== '' && products.length > 1 ? 'cursor:pointer' : ''
              "
            >
              Detalhes do serviço
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items class="grey lighten-3 elevation-0">
            <v-stepper-content step="1" color="white mx-8 my-4">
              <v-card
                color="white"
                class="mx-auto elevation-0 mt-8 grey--text text--darken-1"
                tile
                width="800"
              >
                <v-app-bar
                  outlined
                  dense
                  tile
                  class="ma-0 elevation-0 text-h6 font-weight-regular grey--text text--darken-2"
                >
                  Informações sobre o {{ planOrServiceLabel }}
                </v-app-bar>
                <div class="px-6 py-4">
                  <v-text-field
                    v-model="plan.name"
                    label="Nome*"
                    required
                    outlined
                    color="accent"
                  />
                  <v-textarea
                    v-if="is_service"
                    v-model="plan.description"
                    label="Descrição"
                    maxlength="250"
                    counter
                    no-resize
                    outlined
                    color="accent"
                  />
                  <span class="caption my-2">*campo obrigatório</span>
                </div>
              </v-card>

              <v-card
                color="grey lighten-3"
                class="mx-auto elevation-0"
                width="800"
              >
                <v-card-actions class="float-right px-0 mt-2">
                  <v-btn color="grey" text @click="resetEditAddDialog()">
                    Cancelar
                  </v-btn>
                  <v-btn
                    class="elevation-0 px-4"
                    color="primary"
                    @click="addPlanSteps = 2"
                    :disabled="!plan.name || plan.name.length < 5"
                  >
                    Continuar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>

            <v-stepper-content v-if="!is_service" step="2">
              <v-card
                color="white"
                class="mx-auto elevation-0 mt-4 mb-1"
                tile
                width="1000"
              >
                <v-card-title class="text-subtitle-2" v-text="plan.name" />
              </v-card>

              <v-card
                color="white"
                class="mx-auto elevation-0 mt-4 pb-6 grey--text text--darken-1"
                tile
                width="1000"
              >
                <v-app-bar
                  outlined
                  dense
                  tile
                  class="ma-0 elevation-0 text-h6 font-weight-regular grey--text text--darken-2"
                >
                  Selecionar produtos
                </v-app-bar>
                <v-card outlined tile class="px-0 mx-4 my-8">
                  <v-card-title>
                    Produtos da Conecta
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small icon class="mx-1" v-on="on" v-bind="attrs">
                          <v-icon v-text="'ph-question'" size="16" />
                        </v-btn>
                      </template>
                      <span
                        >Selecione dois ou mais produtos para continuar</span
                      >
                    </v-tooltip>
                  </v-card-title>
                  <v-divider />
                  <v-text-field
                    v-model="searchProduct"
                    placeholder="Pesquisar pelo nome do produto"
                    filled
                    rounded
                    dense
                    background-color="white"
                    clearable
                    class="mb-0 mx-0 mt-1"
                    style="height: 45px"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon class="mr-2" v-text="'ph-magnifying-glass'" />
                    </template>
                  </v-text-field>
                  <v-divider />
                  <v-row>
                    <v-col class="px-6">
                      <v-treeview
                        v-model="products"
                        :search="searchProduct"
                        class="body-2 row_pointer treeview-style"
                        v-if="allProductsToChoose"
                        :items="allProductsToChoose"
                        selectable
                        selected-color="secondary"
                        open-all
                      />
                    </v-col>
                  </v-row>
                </v-card>
              </v-card>

              <v-card
                color="grey lighten-3"
                class="mx-auto elevation-0"
                width="1000"
              >
                <v-card-actions class="d-flex px-0 mt-2">
                  <v-btn text @click="addPlanSteps = 1"> Voltar </v-btn>
                  <v-btn
                    text
                    color="grey"
                    class="ml-auto"
                    @click="resetEditAddDialog()"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    class="elevation-0 px-4"
                    color="primary"
                    @click="addPlanSteps = 3"
                    :disabled="products.length < 2 && !plan.is_basic"
                  >
                    Continuar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>

            <v-stepper-content :step="is_service ? 2 : 3">
              <v-card class="mx-auto elevation-0 mt-4 mb-1" tile width="1000">
                <v-card-title class="text-subtitle-2">
                  {{ plan.name }}
                  <v-spacer />
                  <v-btn icon color="grey" class="grey--text">
                    <v-icon v-text="'medi-pencil'" color="grey" />
                  </v-btn>
                </v-card-title>
              </v-card>

              <v-card
                color="white"
                class="mx-auto elevation-0 mt-4 pb-6 grey--text text--darken-1"
                tile
                width="1000"
              >
                <v-app-bar
                  outlined
                  dense
                  tile
                  class="ma-0 elevation-0 text-h6 font-weight-regular grey--text text--darken-2"
                >
                  Detalhes do {{ planOrServiceLabel }}
                </v-app-bar>
                <v-card outlined class="px-0 mx-4 my-8">
                  <v-card-title
                    class="grey--text text--darken-1 text-subtitle-1 font-weight-medium py-3"
                  >
                    Disponibilidade
                  </v-card-title>
                  <v-divider />
                  <v-row class="d-flex align-center px-4 my-0">
                    <v-col
                      lg="5"
                      md="3"
                      sd="12"
                      class="py-0 my-0 text-subtitle-2 font-weight-regular"
                    >
                      Status
                    </v-col>
                    <v-col lg="7" md="7" sd="12" class="py-0 my-0">
                      <v-switch
                        inset
                        v-model="plan.is_active"
                        :label="`${is_service ? 'Serviço' : 'Plano'} ${
                          plan.is_active ? 'ativo' : 'desativado'
                        }`"
                      ></v-switch>
                    </v-col>
                  </v-row>
                  <v-row v-if="plan.sku" class="d-flex align-center px-4 my-5">
                    <v-col
                      lg="5"
                      md="3"
                      sd="12"
                      class="py-0 my-0 text-subtitle-2 font-weight-regular"
                    >
                      SKU
                    </v-col>
                    <v-col
                      lg="7"
                      md="7"
                      sd="12"
                      class="py-0 my-0 font-weight-regular"
                    >
                      <code>{{ plan.sku }}</code>
                    </v-col>
                  </v-row>

                  <v-row class="d-flex align-center px-4 my-2">
                    <v-col
                      lg="5"
                      md="3"
                      sd="12"
                      class="py-0 my-0 text-subtitle-2 font-weight-regular"
                    >
                      Classificação
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="blue darken-2"
                            dark
                            small
                            v-bind="attrs"
                            v-on="on"
                          >
                            ph-question
                          </v-icon>
                        </template>
                        <span
                          >Determina a ordem de upgrade de planos para sugestões
                          automáticas do sistema.</span
                        >
                      </v-tooltip>
                    </v-col>

                    <v-col
                      lg="7"
                      md="7"
                      sd="12"
                      class="py-0 my-0 font-weight-regular"
                    >
                      <v-combobox
                        v-model="rank"
                        :items="ranks"
                        label="upgrade/downgrade"
                        hint="Faça um upgrade ou downgrade deste plano"
                        min="0"
                        max="40"
                        dense
                        type="number"
                        :rules="[rules.rank]"
                      >
                        <template v-slot:append>
                          <v-icon v-if="rank > plan.rank" color="green"
                            >ph-bold ph-arrow-up</v-icon
                          >
                          <v-icon v-else-if="rank < plan.rank" color="red"
                            >ph-bold ph-arrow-down</v-icon
                          >
                          <v-icon v-else>ph-fill ph-arrows-down-up</v-icon>
                        </template>
                      </v-combobox>
                    </v-col>
                  </v-row>
                  <v-row class="d-flex align-center px-4 my-0">
                    <v-col
                      lg="5"
                      md="3"
                      sd="12"
                      class="py-0 my-0 text-subtitle-2 font-weight-regular"
                    >
                      O cliente pode contratar este {{ planOrServiceLabel }}
                      manualmente
                    </v-col>
                    <v-col lg="7" md="7" sd="12" class="py-0 my-0">
                      <v-switch
                        inset
                        v-model="plan.is_visible"
                        :label="`${
                          plan.is_visible ? 'Visível' : 'Não é visível'
                        }`"
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-card>

                <v-card outlined class="px-0 mx-4 my-8">
                  <v-card-title
                    class="grey--text text--darken-1 text-subtitle-1 font-weight-medium py-3"
                  >
                    {{
                      is_service
                        ? "Valor total do serviço"
                        : "Produtos selecionados"
                    }}
                  </v-card-title>
                  <v-divider v-if="!is_service" />
                  <v-row class="d-flex align-center px-4 my-0">
                    <v-col
                      v-if="!is_service"
                      lg="5"
                      md="3"
                      sd="12"
                      class="py-2 my-0 text-subtitle-2 d-flex flex-column justify-center"
                    >
                      <div
                        v-for="product of products"
                        :key="product"
                        class="font-weight-bold py-1"
                      >
                        {{ productNames[product] }}
                        <span class="font-weight-regular ml-1 caption"
                          >Peso: {{ weights[product] }}</span
                        >
                        <v-chip
                          v-if="['conecta_track'].includes(product)"
                          class="ml-1"
                          x-small
                          >BETA</v-chip
                        >
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row class="px-4 my-0 py-0">
                    <v-col class="d-flex align-center py-0 my-0">
                      <span class="text-h6 font-weight-bold">Total - </span>
                      <vuetify-money
                        v-model="plan.price"
                        :placeholder="'4,99'"
                        :options="moneyOptions"
                        :valueWhenIsEmpty="'0'"
                        class="pl-4 mt-2 pr-1"
                        required
                        dense
                        style="width: 120px"
                      />
                      <span
                        class="text-body-2 font-italic grey--text text--darken-1"
                        >/usuário</span
                      >
                    </v-col>
                  </v-row>
                </v-card>
              </v-card>

              <v-card
                color="grey lighten-3"
                class="mx-auto elevation-0"
                width="1000"
              >
                <v-card-actions class="d-flex px-0 mt-2">
                  <v-btn text @click="addPlanSteps = 2"> Voltar </v-btn>
                  <v-btn
                    text
                    color="grey"
                    class="ml-auto"
                    @click="resetEditAddDialog()"
                  >
                    Cancelar
                  </v-btn>
                  <v-btn
                    class="elevation-0 px-4"
                    color="primary"
                    :disabled="
                      (plan.name === '' && products.length < 2) ||
                      rank < 0 ||
                      rank > 40
                    "
                    @click="showConfirmPlan = true"
                  >
                    Concluir
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>

    <SimpleDialog
      id="confirm_edit_create_plan"
      :title="
        editService
          ? `Confirmar alterações no serviço '${plan.name}'`
          : `Confirmar criação no serviço '${plan.name}'`
      "
      :show="showConfirmPlan"
      :showLoading="loadingTable"
      @close="resetEditAddDialog"
      @action="createOreditService"
      :disabled-action="
        (plan.name === '' && products.length < 2) || loadingTable
      "
      width="650"
    >
      <template v-slot:body>
        <v-row>
          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Nome '"
          />
          <v-col lg="6" md="6" sm="6" v-text="plan.name" />

          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-if="plan.description"
            v-text="'Descrição'"
          />
          <v-col
            lg="6"
            md="6"
            sm="6"
            v-if="plan.description"
            v-text="plan.description"
          />

          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Periodicidade'"
          />
          <v-col lg="6" md="6" sm="6" v-text="planOrServicePeriodicity" />

          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Status'"
          />
          <v-col
            lg="6"
            md="6"
            sm="6"
            v-text="plan.is_active ? 'Ativo' : 'Desativado'"
          />

          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Visível para clientes'"
          />
          <v-col
            v-if="!is_service"
            lg="6"
            md="6"
            sm="6"
            v-text="
              plan.is_visible
                ? 'Visível para todos'
                : 'Somente para revendedores'
            "
          />
          <v-col
            v-else
            lg="6"
            md="6"
            sm="6"
            v-text="'Somente para a conecta nuvem'"
          />

          <v-col
            v-if="!is_service"
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Produtos'"
          />
          <v-col v-if="!is_service" lg="6" md="6" sm="6">
            <div
              v-for="product of products"
              :key="product"
              class="d-flex align-center"
            >
              <span class="font-weight-regular">{{
                productNames[product]
              }}</span>
              <span class="ml-1 caption"> Peso: {{ weights[product] }}</span>
              <v-chip
                v-if="['conecta_track'].includes(product)"
                class="ml-1"
                x-small
                v-text="'BETA'"
              />
            </div>
          </v-col>

          <v-col
            class="font-weight-bold text-subtitle-2"
            lg="6"
            md="6"
            sm="4"
            v-text="'Preço'"
          />
          <v-col
            lg="6"
            md="6"
            sm="6"
            v-text="`${formatMoney(plan.price)}/usuário/mês`"
          />
        </v-row>
      </template>
    </SimpleDialog>

    <Snackbar
      :show="showSnackbar"
      :color="snackbarColor"
      :message="snackbarMessage"
      notShowCloseButton
      :timeout="timeoutSnackbar"
      bottom
      textStyle="font-weight-medium"
    />
  </v-container>
</template>
<script>
import { formatDateFullMonth, formatMoney } from "@/helpers/services/utils";
import { paging, tableHeight } from "@/helpers/variables/tablesDefault";
import { productNames } from "@/helpers/variables/translateString";
import { mapGetters } from "vuex";

export default {
  name: "Services",

  data() {
    return {
      isValid: true,
      showHideServiceDescription: false,
      tableHeight,
      showConfirmPlan: false,
      search: "",
      products: [],
      filters: {
        products: [],
      },
      filterOptions: [
        {
          title: "Produtos",
          key: "products",
          show: true,
          submenu: {
            title: "Produtos",
            options: [],
          },
        },
      ],
      isSuccessMessage: false,
      snackbar: {
        show: false,
        message: "",
      },
      moneyOptions: {
        locale: "pt-BR",
        prefix: "R$",
        suffix: "",
        precision: 2,
      },
      allFrequency: [
        {
          text: "Mensal",
          value: "FLEX",
        },
        {
          text: "Anual",
          value: "ANNUAL",
        },
      ],
      addPlanSteps: 1,
      showHideServiceDialog: false,
      editService: false,
      productNames,
      plan: {
        name: "",
        description: "",
        products: [],
        frequency: ["FLEX", "ANNUAL"],
        is_active: true,
        is_visible: false,
        price: 0,
        rank: 0,
      },
      headers: [
        {
          text: "Nome",
          value: "name",
          width: 200,
          sortable: true,
        },
        {
          text: "Preço",
          value: "price",
          sortable: false,
          align: "right",
        },
        {
          text: "Status",
          value: "is_active",
          sortable: true,
        },
        {
          text: "Visibilidade",
          value: "is_visible",
        },
        {
          text: "Data de criação",
          value: "created_date",
          width: 180,
          sortable: false,
        },
        {
          text: "Classificação",
          value: "rank",
          sortable: true,
          width: 130,
        },
        {
          text: "SKU",
          value: "sku",
          width: 200,
          sortable: false,
        },
        {
          text: "",
          value: "options",
          sortable: false,
          width: 100,
        },
      ],
      items: [
        {
          id: "",
          name: "Conecta Control",
          children: [],
        },
      ],
      ranks: [0, 10, 20, 30, 40],
      rank: 0,
      rules: {
        rank: (value) =>
          (value >= 0 && value <= 40) || "Valores devem estar entre 0 e 40.",
      },
      weights: {
        conecta_suite: 1,
        conecta_control: 6,
        conecta_sign: 2,
        conecta_track: 2,
        conecta_control_mobile: 1,
        conecta_ad_sync: 1,
        integracao_ponto_mais: 1,
        service: 1,
      },
      searchProduct: "",
      loadingTable: false,
      timeoutSnackbar: 5000,
      planReset: {
        name: "",
        description: "",
        products: [],
        frequency: ["FLEX", "ANNUAL"],
        is_visible: false,
        is_active: true,
        price: 0,
        rank: 0,
      },
      itemsPerPage: paging.perPage,
      itemsOptions: paging.All,
      is_service: true,
    };
  },
  async beforeMount() {
    await this.getAllProducts();
  },

  watch: {
    "plan.is_active"() {
      if (this.plan.is_visible && !this.plan.is_active) {
        this.plan.is_visible = false;
      }
    },
    showHideServiceDialog() {
      if (!this.showHideServiceDialog) {
        this.addPlanSteps = 1;
        this.editService = false;
        this.plan = Object.assign({}, this.planReset);
        this.rank = 0;
        this.showConfirmPlan = false;
        this.products = [];
      }
    },
  },
  computed: {
    ...mapGetters(["token", "services"]),

    snackbarMessage() {
      return this.snackbar.message;
    },

    planOrServiceLabel() {
      return this.is_service ? "serviço" : "plano";
    },

    planOrServicePeriodicity() {
      return this.is_service ? "Único" : "Mensal, Anual";
    },

    snackbarColor() {
      return this.isSuccessMessage ? "success" : "error";
    },

    allProductsToChoose() {
      return this.items;
    },

    showSnackbar: {
      get() {
        return this.snackbar.show;
      },
      set(new_value) {
        this.snackbar.show = new_value;
      },
    },

    hasFiltersSelected() {
      return this.filters.products.length > 0;
    },

    allFilters() {
      let total = [];

      let items = Object.entries(this.filters);

      items.forEach((item) => {
        let values = item[1];
        if (values.length > 0) {
          values.forEach((value) => {
            total.push({
              name: value,
              key: item[0],
            });
          });
        }
      });
      return total;
    },
  },
  methods: {
    formatDateFullMonth,

    formatMoney,

    loadSnackbarInfo(hasSucceeded = true) {
      if (hasSucceeded) {
        this.isSuccessMessage = true;
        this.snackbar.message = this.editService
          ? `O plano ${this.plan.name} foi atualizado`
          : `O plano ${this.plan.name} foi criado com sucesso`;
      } else {
        this.isSuccessMessage = false;
        this.snackbar.message = this.editService
          ? `ERRO: o plano ${this.plan.name} não pode ser atualizado.`
          : `ERRO: o plano ${this.plan.name} não pode ser criado.`;
      }
      this.showSnackbar = true;
    },

    createNewPlanOrService(is_service = false) {
      this.is_service = is_service;
      this.showSnackbar = false;
      this.products = [];
      this.plan = Object.assign({}, this.planReset);
      this.showHideServiceDialog = true;
    },

    clearAllFilters() {
      this.filters = {
        products: Array(),
      };
    },

    removeFilter(filter) {
      let index = this.filters[filter.key].indexOf(filter.name);
      this.filters[filter.key].splice(index, 1);
    },

    selectFilter(item) {
      if (!this.filters[item.type].includes(item.filter)) {
        this.filters[item.type].push(item.filter);
      }
    },

    activeOrDeactivePlan(plan) {
      this.plan = Object.assign({}, plan);
      this.changePlanStatus();
    },

    resetEditAddDialog() {
      this.showHideServiceDialog = false;
    },

    selectPlan(chosenPlan) {
      this.is_service = true;
      this.editService = true;
      this.plan = Object.assign({}, chosenPlan);
      this.rank = this.plan.rank;
      let products = this.plan.products.map((product) => product.name);
      this.products = products.slice();
      this.plan.frequency = ["FLEX", "ANNUAL"];
      this.showHideServiceDialog = true;
    },

    async changePlanStatus() {
      this.loadingTable = true;

      let url = process.env.VUE_APP_API_BASE_URL + "/plan";

      const config = {
        headers: { Authorization: this.token },
      };

      const payload = {
        is_active: !this.plan.is_active,
      };

      await this.$axios
        .put(url + `/${this.plan.key}`, payload, config)
        .then(() => {
          const updatedService = { ...this.plan, is_active: payload.is_active };
          this.$store.commit("setUpdatedService", updatedService);
          this.editService = true;
          this.loadSnackbarInfo();
          this.loadingTable = false;
          this.resetEditAddDialog();
        })
        .catch((err) => {
          console.error("createOreditService(): ", err);
          this.editService = true;
          this.loadSnackbarInfo(false);
          this.loadingTable = false;
          this.resetEditAddDialog();
        });
    },

    async createOreditService() {
      this.loadingTable = true;

      let url = process.env.VUE_APP_API_BASE_URL + "/plan";

      const config = {
        headers: { Authorization: this.token },
      };

      if (this.editService) {
        this.plan.products = this.products.map((product) => {
          return {
            name: product,
            weight: this.weights[product],
          };
        });

        this.plan.price = parseFloat(this.plan.price);
        this.plan.rank = parseInt(this.rank);

        await this.$axios
          .put(url + `/${this.plan.key}`, this.plan, config)
          .then(() => {
            this.$store.commit("setUpdatedService", { ...this.plan });
            this.loadSnackbarInfo();
            this.loadingTable = false;
            this.resetEditAddDialog();
          })
          .catch((err) => {
            console.error("createOreditService(): ", err);
            this.loadSnackbarInfo(false);
            this.loadingTable = false;
            this.resetEditAddDialog();
          });
      } else {
        const payload = {
          name: this.plan.name,
          description: this.plan.description,
          price: parseFloat(this.plan.price),
          products: this.products.map((product) => {
            return {
              name: product,
              weight: this.weights[product],
            };
          }),
          is_visible: this.plan.is_visible,
          is_active: this.plan.is_active,
          rank: parseInt(this.rank),
          is_service: this.is_service,
        };

        await this.$axios
          .post(url, payload, config)
          .then(({ data }) => {
            this.$store.commit("setNewService", data.message);
            this.loadSnackbarInfo();
            this.loadingTable = false;
            this.resetEditAddDialog();
          })
          .catch((err) => {
            console.error("createOreditService(): ", err);
            this.loadSnackbarInfo(false);
            this.loadingTable = false;
            this.resetEditAddDialog();
          });
      }
    },

    async getAllProducts() {
      let url = process.env.VUE_APP_API_BASE_URL + "/plans/products";

      const config = {
        headers: { Authorization: this.token },
      };

      await this.$axios
        .get(url, config)
        .then(({ data }) => {
          this.filterOptions[0].submenu.options = data.map((item) => {
            return {
              title: productNames[item.name],
              name: item.name,
              show: true,
            };
          });
          data.forEach((product) => {
            this.weights[product.name] = product.weight;
            if (product.name === "conecta_control") {
              this.items[0].children.push({
                id: product.name,
                name: productNames[product.name],
              });
              this.items[0].children.push({
                id: "conecta_control_mobile",
                name: productNames["conecta_control_mobile"],
              });
              this.items[0].children.push({
                id: "conecta_ad_sync",
                name: productNames["conecta_ad_sync"],
              });
              this.items[0].children.push({
                id: "integracao_ponto_mais",
                name: productNames["integracao_ponto_mais"],
              });
            } else {
              if (
                ![
                  "conecta_ad_sync",
                  "integracao_ponto_mais",
                  "conecta_control_mobile",
                  "conecta_control",
                ].includes(product.name)
              ) {
                this.items.push({
                  id: product.name,
                  name: productNames[product.name],
                });
              }
            }
          });
        })
        .catch((err) => {
          console.error("getAllProducts(): ", err);
        });
    },
  },
};
</script>
<style scoped>
.v-stepper__step {
  padding: 0px;
}
.v-stepper {
  -webkit-box-shadow: 0px 0px 0px 0px rgb(0 0 0 / 20%),
    0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 0px 0px 0px rgb(0 0 0 / 12%) !important;
  box-shadow: 0px 0px 0px 0px rgb(0 0 0 / 20%), 0px 0px 0px 0px rgb(0 0 0 / 14%),
    0px 0px 0px 0px rgb(0 0 0 / 12%) !important;
}
</style>
